import { zodResolver } from '@hookform/resolvers/zod'
import { type LoginRequest, LoginRequestSchema } from '@gestao/shared'
import { Button, Checkbox, FormField, Input, PasswordInput } from '@gestao/ui'
import { useEffect, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { Link } from 'react-router'
import type { z } from 'zod'

import { FormAlert } from '../../components/form-alert'
import { authLinkClasses } from './auth-link'
import * as t from '../../i18n/pt-BR'
import { errorMessage, fieldErrors, isApiError } from '../../lib/api'
import { canAutoFocus } from '../../lib/auto-focus'
import { signIn } from './api'

type LoginFormInput = z.input<typeof LoginRequestSchema>

const FIELDS = ['email', 'password'] as const

/**
 * Formulário de login (design-system §6): e-mail, senha com mostrar/ocultar, "Lembrar este
 * dispositivo", botão de largura total e "Esqueci minha senha". Foco inicial no e-mail, Enter
 * envia, erros abaixo do campo sem apagar o que foi digitado. Quem redireciona depois do
 * sucesso é a página, ao ver a sessão autenticada.
 */
export function LoginForm() {
  const [formError, setFormError] = useState<string | null>(null)
  const {
    control,
    register,
    handleSubmit,
    setError,
    setFocus,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormInput, unknown, LoginRequest>({
    resolver: zodResolver(LoginRequestSchema),
    defaultValues: { email: '', password: '', rememberDevice: false },
  })
  const email = useWatch({ control, name: 'email' })

  useEffect(() => {
    if (canAutoFocus()) setFocus('email')
  }, [setFocus])

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null)
    try {
      await signIn(values)
    } catch (error) {
      if (isApiError(error, 'AUTH_INVALID_CREDENTIALS')) {
        setError('password', { message: error.message }, { shouldFocus: true })
        return
      }
      const byField = fieldErrors(error)
      const invalidFields = FIELDS.filter((field) => byField[field])
      if (invalidFields.length > 0) {
        for (const field of invalidFields) setError(field, { message: byField[field] })
        setFocus(invalidFields[0] ?? 'email')
        return
      }
      setFormError(errorMessage(error))
    }
  })

  return (
    <form noValidate onSubmit={onSubmit} className="flex flex-col gap-6">
      <h1 className="text-page text-ink">{t.auth.login.title}</h1>

      <div className="flex flex-col gap-4">
        <FormField label={t.auth.email} error={errors.email?.message}>
          <Input
            type="email"
            inputMode="email"
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            className="h-10"
            {...register('email')}
          />
        </FormField>

        <FormField label={t.auth.password} error={errors.password?.message}>
          <PasswordInput
            autoComplete="current-password"
            toggleLabel={t.auth.showPassword}
            className="h-10"
            {...register('password')}
          />
        </FormField>

        <Controller
          control={control}
          name="rememberDevice"
          render={({ field }) => (
            <Checkbox
              ref={field.ref}
              name={field.name}
              label={t.auth.login.remember}
              checked={field.value === true}
              onCheckedChange={(checked) => field.onChange(checked === true)}
              onBlur={field.onBlur}
            />
          )}
        />
      </div>

      {formError ? <FormAlert>{formError}</FormAlert> : null}

      <div className="flex flex-col gap-4">
        <Button type="submit" loading={isSubmitting} className="h-10 w-full">
          {t.auth.login.submit}
        </Button>
        <Link to="/esqueci-senha" state={{ email }} className={authLinkClasses}>
          {t.auth.login.forgot}
        </Link>
      </div>
    </form>
  )
}
